import React, { memo, useCallback, useEffect } from 'react'
import { makeStyles } from '@material-ui/styles'
import { useTranslation } from 'react-i18next'
import {
	TextField,
	Grid,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
} from '@material-ui/core'
import * as _ from 'lodash'
import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import Fab from '@material-ui/core/Fab'
import classNames from 'classnames'

import { EJobActionTypes, EJobTextCategory } from 'models'
import { StoreValue } from 'store'
import styles from '../styles'
import { SearchSelect, JobEditor } from './'
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDownOutlined'
import {
	useCompanyOfficesQuery,
	useSkillCollectionQuery,
} from 'graphql/__generated__/graphql'

const useStyles = makeStyles(styles)
const contractTypes: { [key: string]: any } = {
	FULL_TIME: 'Full Time',
	PART_TIME: 'Part Time',
	ONE_TIME: 'One Time',
	PROJECT_BASED: 'Project based',
}
const jobTypes: { [key: string]: any } = {
	INTERNSHIP: 'Internship',
	INTERN: 'Intern',
	FREELANCE: 'Freelance',
}
const jobTypeLocList: { [key: string]: any } = {
	ON_SITE: 'OnSite',
	REMOTE: 'Remote',
}

const seniorityLevelList: { [key: string]: any } = {
	BEGINNER: 'Beginner',
	MIDDLE: 'Middle',
	SENIOR: 'Senior',
}
const Step2View: React.FunctionComponent<any> = props => {
	const { job, storeDispatch, handleBack, handleNext } = props
	const {
		salary,
		contractType,
		jobType,
		jobTypeLoc,
		error,
		seniorityLevel,
		defaultCompanyOffices,
		skills,
		searchForDefaultCompanyOffices,
		locations,
		jobCompany,
		benefits,
	} = job

	const classes = useStyles()
	const { t } = useTranslation()
	const currencies = ['Eur', 'Usd', 'Ron']
	const rateList = ['Monthly', 'Hourly']

	const { refetch: refetchSkills } = useSkillCollectionQuery({
		skip: true,
		fetchPolicy: 'no-cache',
	})

	const { refetch: refetchLocations } = useCompanyOfficesQuery({
		skip: true,
		fetchPolicy: 'no-cache',
	})

	const scrollToErrorField = useCallback(
		(errorFields: {
			skills: boolean
			benefits: boolean
			locations: boolean
			contractType: boolean
			jobType: boolean
			jobTypeLoc: boolean
			seniorityLevel: boolean
			salary: {
				amountMin: boolean
				amountMax: boolean
				currency: boolean
				rate: boolean
			}
		}) => {
			// I need to scroll to the highest error field.
			let elementToScrollTo: HTMLElement | null
			const {
				salary: { amountMin, amountMax, currency, rate },
				skills,
				benefits,
				locations,
				contractType,
				jobType,
				jobTypeLoc,
				seniorityLevel,
			} = errorFields

			if (!amountMin || amountMax || !currency || !rate) {
				elementToScrollTo = document.querySelector('#salaryRange')
			} else if (skills) {
				elementToScrollTo = document.querySelector('#skills')
			} else if (benefits) {
				elementToScrollTo = document.querySelector('#benefits')
			} else if (locations) {
				elementToScrollTo = document.querySelector('#locations')
			} else if (contractType) {
				elementToScrollTo = document.querySelector('#contractType')
			} else if (jobType) {
				elementToScrollTo = document.querySelector('#jobType')
			} else if (jobTypeLoc) {
				elementToScrollTo = document.querySelector('#jobTypeLoc')
			} else if (seniorityLevel) {
				elementToScrollTo = document.querySelector('#seniorityLevel')
			} else {
				elementToScrollTo = null
			}

			elementToScrollTo?.scrollIntoView({
				behavior: 'smooth',
			})
		},
		[],
	)

	const isNotValidStep = useCallback(() => {
		let isSalaryInvalid = true
		const { amountMin, amountMax, currency, rate } = salary

		if (
			(!amountMin && !amountMax && !currency && !rate) ||
			(amountMin && amountMax && currency && rate)
		) {
			// if all salary fields are empty or filled
			isSalaryInvalid = false
		}
		return (
			_.isEmpty(skills) ||
			_.isEmpty(benefits) ||
			_.isEmpty(locations) ||
			_.isEmpty(contractType) ||
			_.isEmpty(jobType) ||
			_.isEmpty(jobTypeLoc) ||
			_.isEmpty(seniorityLevel) ||
			isSalaryInvalid
		)
	}, [
		contractType,
		jobType,
		jobTypeLoc,
		seniorityLevel,
		benefits,
		locations,
		skills,
		salary,
	])

	const goToNextStep = useCallback(
		_error => {
			if (isNotValidStep()) {
				// check for salary validation. Salary is optional, but if the user complete one field from salary section all other fields that are related with salary will become mandatory
				let salaryValidation: {
					amountMin: boolean
					amountMax: boolean
					currency: boolean
					rate: boolean
					[key: string]: any
				} = {
					amountMin: false,
					amountMax: false,
					currency: false,
					rate: false,
				}

				let checkIfAllSalaryFieldsAreEmpty = true
				for (let item in salary) {
					salaryValidation[item] = !salary[item]
					if (!!salary[item]) {
						checkIfAllSalaryFieldsAreEmpty = false
					}
				}
				if (checkIfAllSalaryFieldsAreEmpty) {
					// if all the fields are empty I will not throw an error
					salaryValidation = {
						amountMin: false,
						amountMax: false,
						currency: false,
						rate: false,
					}
				}

				storeDispatch({
					type: EJobActionTypes.setError,
					errors: {
						skills: _.isEmpty(skills),
						benefits: _.isEmpty(benefits),
						locations: _.isEmpty(locations),
						contractType: _.isEmpty(contractType),
						jobType: _.isEmpty(jobType),
						jobTypeLoc: _.isEmpty(jobTypeLoc),
						seniorityLevel: _.isEmpty(seniorityLevel),
						salary: salaryValidation,
					},
				})
				scrollToErrorField({
					skills: _.isEmpty(skills),
					benefits: _.isEmpty(benefits),
					locations: _.isEmpty(locations),
					contractType: _.isEmpty(contractType),
					jobType: _.isEmpty(jobType),
					jobTypeLoc: _.isEmpty(jobTypeLoc),
					seniorityLevel: _.isEmpty(seniorityLevel),
					salary: salaryValidation,
				})
				return
			}
			handleNext()
		},
		[
			scrollToErrorField,
			handleNext,
			isNotValidStep,
			storeDispatch,
			contractType,
			jobType,
			jobTypeLoc,
			seniorityLevel,
			benefits,
			locations,
			skills,
			salary,
		],
	)
	const chooseCurrency = useCallback(
		event => {
			storeDispatch({
				type: EJobActionTypes.setCurrency,
				currency: event.target.value,
			})
		},
		[storeDispatch],
	)
	const chooseSalaryRate = useCallback(
		event => {
			storeDispatch({
				type: EJobActionTypes.setSalaryRate,
				rate: event.target.value,
			})
		},
		[storeDispatch],
	)

	const updateStartSalary = useCallback(
		event => {
			storeDispatch({
				type: EJobActionTypes.setStartSalary,
				startSalary: event.target.value,
			})
		},
		[storeDispatch],
	)
	const updateEndSalary = useCallback(
		event => {
			storeDispatch({
				type: EJobActionTypes.setEndSalary,
				endSalary: event.target.value,
			})
		},
		[storeDispatch],
	)
	const updateContractType = useCallback(
		contractType => {
			storeDispatch({
				type: EJobActionTypes.setContractType,
				contractType: contractType,
			})
		},
		[storeDispatch],
	)
	const updateJobType = useCallback(
		jobType => {
			storeDispatch({
				type: EJobActionTypes.setJobType,
				jobType: jobType,
			})
		},
		[storeDispatch],
	)
	const updateJobTypeLoc = useCallback(
		jobTypeLoc => {
			storeDispatch({
				type: EJobActionTypes.setJobTypeByLoc,
				jobTypeLoc: jobTypeLoc,
			})
		},
		[storeDispatch],
	)
	const updateSeniorityLevel = useCallback(
		seniorityLevel => {
			storeDispatch({
				type: EJobActionTypes.setSeniorityLevel,
				seniorityLevel: seniorityLevel,
			})
		},
		[storeDispatch],
	)

	const handleChangeMultiSkills = useCallback(
		value => {
			storeDispatch({
				type: EJobActionTypes.setSkills,
				skills: value ? value : [],
			})
		},
		[storeDispatch],
	)

	const handleChangeMultiLocations = useCallback(
		value => {
			storeDispatch({
				type: EJobActionTypes.setLocations,
				locations: value ? value : [],
			})
		},
		[storeDispatch],
	)

	const debouncedFetchSkills = useCallback(
		_.debounce((searchTerm: any, callback: any) => {
			refetchSkills({ key: searchTerm })
				.then((result: any) => {
					return callback(
						result?.data?.searchSkills?.map((item: any) => {
							let tempItem = {
								value: '',
								label: '',
							}
							tempItem.value = item.id
							tempItem.label = item.title
							return tempItem
						}),
					)
				})
				.catch((error: any) => callback(error, null))
		}, 500),
		[],
	)

	const debouncedFetchLocations = useCallback(
		_.debounce((searchTerm: any, callback: any) => {
			refetchLocations({
				// name: searchTerm,
				companySlug: jobCompany ? jobCompany.slug : '',
			})
				.then((result: any) => {
					if (result) {
						return callback(
							result?.data?.companyOffices?.map((item: any) => {
								let tempItem = {
									value: '',
									label: '',
								}
								tempItem.value = item.id
								tempItem.label = item.location.name
								return tempItem
							}),
						)
					} else {
						return callback([])
					}
				})
				.catch((error: any) => callback(error, null))
		}, 500),
		[jobCompany],
	)

	const getSkillsOptions = useCallback(
		(input: any, callback: any) => {
			if (!input) {
				return Promise.resolve({ options: [] })
			}
			debouncedFetchSkills(input, callback)
		},
		[debouncedFetchSkills],
	)

	const getLocationsOptions = useCallback(
		(input: any, callback: any) => {
			if (!input) {
				return Promise.resolve({ options: [] })
			}
			debouncedFetchLocations(input, callback)
		},
		[debouncedFetchLocations],
	)

	useEffect(() => {
		if (jobCompany && searchForDefaultCompanyOffices) {
			refetchLocations({
				companySlug: jobCompany.slug,
			})
				.then((result: any) => {
					if (result) {
						const companyOfficeData =
							result?.data?.companyOffices?.map((item: any) => {
								let tempItem = {
									value: '',
									label: '',
								}
								tempItem.value = item.id
								tempItem.label = item.location.name
								return tempItem
							})
						storeDispatch({
							type: EJobActionTypes.setSearchForDefaultCompanyOffices,
							value: false,
							data: companyOfficeData,
						})
					} else {
						return []
					}
				})
				.catch((error: any) => console.error(error))
		}
	}, [
		jobCompany,
		refetchLocations,
		storeDispatch,
		searchForDefaultCompanyOffices,
	])

	useEffect(() => {
		// when the user empties all the fields that are related with salary, reset all the validation from salary
		if (
			!salary.amountMin &&
			!salary.amountMax &&
			!salary.currency &&
			!salary.rate &&
			(error.salary.amountMin ||
				error.salary.amountMax ||
				error.salary.currency ||
				error.salary.rate)
		) {
			storeDispatch({
				type: EJobActionTypes.setError,
				errors: {
					salary: {
						amountMin: false,
						amountMax: false,
						currency: false,
						rate: false,
					},
				},
			})
		}
	}, [salary, error.salary, storeDispatch])

	return (
		<div>
			<Grid container direction={'row'} spacing={2}>
				<Grid item xs={12} className={classes.titleField}>
					<div id="salaryRange" className={classes.labelTextField}>
						{t('Define the salary range')}
						<span className={classes.requiredField}>
							{`(${t(
								'Optional: If you fill in a field, it is necessary to fill in all the fields',
								{ keySeparator: '>', nsSeparator: '|' },
							)})`}
						</span>
					</div>
				</Grid>
				<Grid
					item={true}
					sm={3}
					xs={6}
					className={classes.salaryRangeItem}
				>
					<TextField
						label={t('Starting from')}
						InputLabelProps={{
							className: classes.labelTextField,
						}}
						InputProps={{
							className: classes.input,
						}}
						type="number"
						error={error.salary.amountMin}
						className={classes.textField}
						value={salary.amountMin}
						onChange={updateStartSalary}
						fullWidth
					/>
				</Grid>
				<Grid
					item={true}
					sm={3}
					xs={6}
					className={classes.salaryRangeItem}
				>
					<span></span>
					<TextField
						label={t('To')}
						InputLabelProps={{
							className: classes.labelTextField,
						}}
						InputProps={{
							className: classes.input,
						}}
						type="number"
						error={error.salary.amountMax}
						className={classes.textField}
						value={salary.amountMax}
						onChange={updateEndSalary}
						fullWidth
					/>
				</Grid>
				<Grid
					item={true}
					sm={3}
					xs={6}
					className={classes.salaryRangeItem}
				>
					<FormControl
						className={classes.formControl}
						error={error.salary.currency}
					>
						<InputLabel htmlFor="currency-filed">
							{t('Currency')}
						</InputLabel>
						<Select
							value={salary.currency}
							onChange={chooseCurrency}
							IconComponent={KeyboardArrowDown}
							inputProps={{
								name: 'currency',
								id: 'currency-filed',
								className: classes.input,
							}}
						>
							<MenuItem value="">
								<em>{t('None')}</em>
							</MenuItem>

							{currencies?.map((item: any) => {
								return (
									<MenuItem key={item} value={item}>
										{item.toUpperCase()}
									</MenuItem>
								)
							})}
						</Select>
					</FormControl>
				</Grid>
				<Grid
					item={true}
					sm={3}
					xs={6}
					className={classes.salaryRangeItem}
				>
					<FormControl
						className={classes.formControl}
						error={error.salary.rate}
					>
						<InputLabel htmlFor="rate-filed">
							{t('Rate')}
						</InputLabel>
						<Select
							value={
								salary.rate &&
								`${salary.rate.substring(0, 1)}${salary.rate
									.toLowerCase()
									.substring(1)}`
							}
							onChange={chooseSalaryRate}
							inputProps={{
								name: 'rate',
								id: 'rate-filed',
								className: classes.input,
							}}
							IconComponent={KeyboardArrowDown}
						>
							<MenuItem value="">
								<em>{t('None')}</em>
							</MenuItem>

							{rateList?.map((item: any) => {
								return (
									<MenuItem key={item} value={item}>
										{item[0] +
											item.toLowerCase().substring(1)}
									</MenuItem>
								)
							})}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={12} className={classes.fieldContainer}>
					<div
						id="skills"
						className={classNames(
							classes.labelTextField,
							classes.mt10,
							{
								[classes.labelFieldError]: error.skills,
							},
						)}
					>
						{t('Add Required Skills. Search from our database ...')}
						<span className={classes.requiredField}>
							{`(${t('Required')})`}
						</span>
					</div>
					<SearchSelect
						handleChange={handleChangeMultiSkills}
						loadOptions={getSkillsOptions}
						items={skills}
						placeholderText={t('Search for required skills...')}
						isMulti={true}
					/>
				</Grid>

				<Grid item xs={12} className={classes.fieldContainer}>
					<div id="benefits" className={classes.editorRoot}>
						<div
							className={classNames(classes.labelTextField, {
								[classes.labelFieldError]: error.benefits,
							})}
						>
							{t('Benefits.')}
							<span className={classes.requiredField}>
								{`(${t('Required')})`}
							</span>
						</div>
						<JobEditor textCategory={EJobTextCategory.benefits} />
					</div>
				</Grid>
				<Grid item xs={12} className={classes.fieldContainer}>
					<div
						id="locations"
						className={classNames(classes.labelTextField, {
							[classes.labelFieldError]: error.locations,
						})}
					>
						{t('Add locations')}
						<span className={classes.requiredField}>
							{`(${t('Required')})`}
						</span>
					</div>
					<SearchSelect
						handleChange={handleChangeMultiLocations}
						loadOptions={getLocationsOptions}
						defaultOptions={defaultCompanyOffices}
						items={locations}
						placeholderText={t('Search for locations')}
						isMulti={true}
					/>
				</Grid>
				<Grid item xs={12} className={classes.fieldContainer}>
					<div
						id="contractType"
						className={classNames(classes.labelTextField, {
							[classes.labelFieldError]: error.contractType,
						})}
					>
						{t('What is the Contract type?')}
						<span className={classes.requiredField}>
							{`(${t('Required')})`}
						</span>
					</div>
					<div className={classes.selectionContainer}>
						{contractTypes &&
							Object.keys(contractTypes)?.map(
								(item: any, index: any) => {
									return (
										<Chip
											key={'contractType' + index}
											variant="outlined"
											className={`${classes.chip} ${
												item === contractType
													? classes.chipActive
													: classes.chipInactive
											} `}
											label={contractTypes[item]}
											size={'small'}
											clickable={true}
											onClick={() =>
												updateContractType(item)
											}
										/>
									)
								},
							)}
					</div>
				</Grid>
				<Grid item xs={12} className={classes.fieldContainer}>
					<div
						id="jobType"
						className={classNames(classes.labelTextField, {
							[classes.labelFieldError]: error.jobType,
						})}
					>
						{t('What kind of job is this?')}
						<span className={classes.requiredField}>
							{`(${t('Required')})`}
						</span>
					</div>
					<div className={classes.selectionContainer}>
						{jobTypes &&
							Object.keys(jobTypes)?.map(
								(item: any, index: any) => {
									return (
										<Chip
											key={'jobType' + index}
											variant="outlined"
											className={`${classes.chip} ${
												item === jobType
													? classes.chipActive
													: classes.chipInactive
											} `}
											label={jobTypes[item]}
											size={'small'}
											clickable={true}
											onClick={() => updateJobType(item)}
										/>
									)
								},
							)}
					</div>
				</Grid>
				<Grid item xs={12} className={classes.fieldContainer}>
					<div
						id="jobTypeLoc"
						className={classNames(classes.labelTextField, {
							[classes.labelFieldError]: error.jobTypeLoc,
						})}
					>
						{t('Is this job On site or Remote?')}
						<span className={classes.requiredField}>
							{`(${t('Required')})`}
						</span>
					</div>
					<div className={classes.selectionContainer}>
						{jobTypeLocList &&
							Object.keys(jobTypeLocList)?.map((item: any) => {
								return (
									<Chip
										key={'jobTypeLoc' + item}
										variant="outlined"
										className={classNames(classes.chip, {
											[classes.chipActive]:
												jobTypeLoc.includes(item),
											[classes.chipInactive]:
												!jobTypeLoc.includes(item),
										})}
										label={jobTypeLocList[item]}
										size={'small'}
										clickable={true}
										onClick={() => updateJobTypeLoc(item)}
									/>
								)
							})}
					</div>
				</Grid>
				<Grid item xs={12} className={classes.fieldContainer}>
					<div
						className={classNames(classes.labelTextField, {
							[classes.labelFieldError]: error.seniorityLevel,
						})}
					>
						{t('What Seniority level are you looking for?')}
						<span className={classes.requiredField}>
							{`(${t('Required')})`}
						</span>
					</div>
					<div className={classes.selectionContainer}>
						{seniorityLevelList &&
							Object.keys(seniorityLevelList)?.map(
								(item: any, index: any) => {
									return (
										<Chip
											key={'seniority' + index}
											variant="outlined"
											className={`${classes.chip} ${
												item === seniorityLevel
													? classes.chipActive
													: classes.chipInactive
											} `}
											label={seniorityLevelList[item]}
											size={'small'}
											clickable={true}
											onClick={() =>
												updateSeniorityLevel(item)
											}
										/>
									)
								},
							)}
					</div>
				</Grid>
				<Grid item xs={12}>
					<div className={classes.btnsContainer}>
						<Button
							onClick={handleBack}
							// className={classes.button}
						>
							{t('Back')}
						</Button>
						{/* <Button onClick={handleSkip}>{t('Skip')}</Button> */}
						<Fab
							onClick={goToNextStep}
							className={classes.nextBtn}
							variant="extended"
							size="medium"
							color="primary"
							aria-label="Add"
						>
							{t('Next') || ''}
						</Fab>
					</div>
				</Grid>
			</Grid>
		</div>
	)
}

const Step2 = memo((props: any) => (
	<StoreValue keys={['job', 'storeDispatch']}>
		<Step2View {...props} />
	</StoreValue>
))

export default Step2
